import { Alert } from '@ksyos/design-system';
import { Fragment } from 'react';
import type { ICallBackMomentAddEdit, ITimeRange } from '../../../../../common/validation/callBackMoment';
import type { ValidationErrors } from '../../../../utils/validation';
import { translateKey } from '../../../../translate';
import { getValidationErrorTranslation } from '../../../../utils/getValidationErrorTranslation';
import { CheckboxInput } from '../../../fields/Checkbox';
import { TimeInput } from '../../../fields/Time';
import { Feedback } from '../../common';
import { FormRow } from '../../forms/FormRow';
import styles from './CallBackMomentAddEdit.module.css';

interface ICallBackMomentAddEditProps {
    callBackMoments: ICallBackMomentAddEdit[];
    errors: ValidationErrors;
    handleChange: (callBackMoments: ICallBackMomentAddEdit[]) => Promise<void>;
    onDoValidate: (key: string) => Promise<void>;
}

export function CallBackMomentAddEdit(props: ICallBackMomentAddEditProps) {
    const defaultStartTime = '09:00';
    const defaultEndTime = '17:00';

    return (
        <>
            <table className={styles.table}>
                <tbody>
                    {props.callBackMoments.map((callbackMoment, index) => (
                        <Fragment key={index}>
                            <tr>
                                <td className={styles.day}>
                                    <CheckboxInput
                                        checked={callbackMoment.isChecked}
                                        onChange={async (value) => await handleChange('isChecked', value, index)}
                                        onDoValidate={async () => await doValidateBeschikbaarheid(index)}
                                        text={translateKey('daysEnum.' + callbackMoment.dayOfWeek)}
                                    />
                                </td>
                                <td className={styles.timeOfDay}>
                                    {renderStartAndEndTimeRow(callbackMoment, 'periodOne', index)}
                                </td>
                                <td className={styles.text}>en</td>
                                <td className={styles.timeOfDay}>
                                    {renderStartAndEndTimeRow(callbackMoment, 'periodTwo', index)}
                                </td>
                            </tr>
                            <tr>
                                <td />
                                <td>{renderFeedback(`customControlData.beschikbaarheid.${index}.periodOne`)}</td>
                                <td />
                                <td>{renderFeedback(`customControlData.beschikbaarheid.${index}.periodTwo`)}</td>
                            </tr>
                        </Fragment>
                    ))}
                </tbody>
            </table>
            {renderAlert('customControlData.beschikbaarheid')}
        </>
    );

    function renderStartAndEndTimeRow(
        callBackMoment: ICallBackMomentAddEdit,
        period: 'periodOne' | 'periodTwo',
        index: number
    ) {
        return (
            <FormRow
                className="layout center"
                hasErrors={
                    props.errors.has(`customControlData.beschikbaarheid.${index}.${period}.startTime`) ||
                    props.errors.has(`customControlData.beschikbaarheid.${index}.${period}.endTime`)
                }
            >
                <div className="col-lg-10 col-xl-8">
                    <TimeInput
                        className="pl-1 pr-1"
                        disabled={!callBackMoment.isChecked}
                        error={props.errors.get(`customControlData.beschikbaarheid.${index}.${period}.startTime`)}
                        onChange={async (value) => handleTimeChange(period, 'startTime', index, value)}
                        onDoValidate={async () => props.onDoValidate(`beschikbaarheid.${index}.${period}.startTime`)}
                        placeholder={defaultStartTime}
                        value={callBackMoment[period].startTime}
                    />
                </div>
                <div className="col-lg-4 col-xl-8 text-center">
                    <span>tot</span>
                </div>
                <div className="col-lg-10 col-xl-8">
                    <TimeInput
                        className="pl-1 pr-1"
                        disabled={!callBackMoment.isChecked}
                        error={props.errors.get(`customControlData.beschikbaarheid.${index}.${period}.endTime`)}
                        onChange={async (value) => handleTimeChange(period, 'endTime', index, value)}
                        onDoValidate={async () => props.onDoValidate(`beschikbaarheid.${index}.${period}.endTime`)}
                        placeholder={defaultEndTime}
                        value={callBackMoment[period].endTime}
                    />
                </div>
            </FormRow>
        );
    }

    function renderFeedback(errorKey: string) {
        return (
            <FormRow hasErrors={props.errors.has(errorKey)}>
                <Feedback error={props.errors.get(errorKey)} />
            </FormRow>
        );
    }

    function renderAlert(errorKey: string) {
        if (!props.errors.has(errorKey)) {
            return null;
        }

        return (
            <FormRow hasErrors={props.errors.has(errorKey)}>
                <Alert
                    appearance="warning"
                    noMargin={true}
                >
                    {getValidationErrorTranslation(props.errors.get(errorKey))}
                </Alert>
            </FormRow>
        );
    }

    async function handleTimeChange(
        period: 'periodOne' | 'periodTwo',
        field: 'startTime' | 'endTime',
        index: number,
        value: string | null
    ) {
        const newCallbackMoments: ICallBackMomentAddEdit[] = [...props.callBackMoments];
        const time = value === '' ? null : value;
        newCallbackMoments[index] = {
            ...newCallbackMoments[index],
            [period]: { ...newCallbackMoments[index][period], [field]: time },
        };
        props.handleChange(newCallbackMoments);
    }

    async function handleChange<K extends keyof ICallBackMomentAddEdit>(
        field: K,
        value: ICallBackMomentAddEdit[K],
        index: number
    ) {
        if (field === 'periodOne' || field === 'periodTwo') {
            return;
        }

        const periodOne: ITimeRange = props.callBackMoments[index].periodOne;
        if (field === 'isChecked' && value === true && noTimePeriodsSet(props.callBackMoments[index])) {
            periodOne.startTime = defaultStartTime;
            periodOne.endTime = defaultEndTime;
        }

        const newCallbackMoments: ICallBackMomentAddEdit[] = [...props.callBackMoments];
        newCallbackMoments[index] = { ...newCallbackMoments[index], periodOne, [field]: value };

        props.handleChange(newCallbackMoments);
    }

    function noTimePeriodsSet(callBackMoment: ICallBackMomentAddEdit) {
        return isEmptyPeriod(callBackMoment.periodOne) && isEmptyPeriod(callBackMoment.periodOne);
    }

    function isEmptyPeriod(period: ITimeRange) {
        return !period.startTime && !period.endTime;
    }

    async function doValidateBeschikbaarheid(index: number) {
        props.onDoValidate('beschikbaarheid');
        if (props.errors.has(`customControlData.beschikbaarheid.${index}.periodOne.startTime`)) {
            props.onDoValidate(`beschikbaarheid.${index}.periodOne.startTime`);
        }
        if (props.errors.has(`customControlData.beschikbaarheid.${index}.periodOne.endTime`)) {
            props.onDoValidate(`beschikbaarheid.${index}.periodOne.endTime`);
        }
        if (props.errors.has(`customControlData.beschikbaarheid.${index}.periodTwo.startTime`)) {
            props.onDoValidate(`beschikbaarheid.${index}.periodTwo.startTime`);
        }
        if (props.errors.has(`customControlData.beschikbaarheid.${index}.periodTwo.endTime`)) {
            props.onDoValidate(`beschikbaarheid.${index}.periodTwo.endTime`);
        }
        if (props.errors.has(`customControlData.beschikbaarheid.${index}.periodOne`)) {
            props.onDoValidate(`beschikbaarheid.${index}.periodOne.startTime`);
        }
        if (props.errors.has(`customControlData.beschikbaarheid.${index}.periodTwo`)) {
            props.onDoValidate(`beschikbaarheid.${index}.periodTwo.startTime`);
        }
    }
}
